import appendItem from '../util/appendItem';
import { ElevenAgentData } from '../ecdn11/interfaces';
import { PingData, PingResponse } from './probeUrls';
import { NetworkData } from './networkDataStorage';
import { PlayType } from '@kollective-frontend/ksdk-api';

export interface LocalityGatherDuration {
  '11': number;
  ICE: number;
  total: number;
}

export const DEFAULT_DURATIONS = Object.freeze({
  '11': -1,
  ICE: -1,
  ping: -1,
  total: -1
});

export interface LocalityConstructor {
  eleven?: ElevenAgentData;
  gatherDuration?: LocalityGatherDuration;
  ice?: RTCIceCandidateInit[];
  probe?: PingData;
  userEmail?: string;
  eventTitle?: string;
}

const mergeInAddresses = (
  agentData: ILocality,
  eleven: ElevenAgentData
): void => {
  agentData.externalIPs = appendItem(eleven.publicIp);

  if (!eleven.interfaces) {
    return;
  }

  // TODO SRV-4425 filter out status 'off' interfaces.
  Object.values(eleven.interfaces)
    .filter(i => i.status === 'on')
    .forEach(value => {
      if (value.default == 'true') {
        // Put the defaults at the beginning of the list.
        agentData.internalIPs = appendItem(
          value.addresses,
          agentData.internalIPs
        );
        agentData.gatewayIPs = appendItem(value.gateways, agentData.gatewayIPs);
      } else {
        agentData.internalIPs = appendItem(
          agentData.internalIPs,
          value.addresses
        );
        agentData.gatewayIPs = appendItem(agentData.gatewayIPs, value.gateways);
      }
    });
};

export interface ILocality {
  machineName?: string;
  machineUser?: string;
  userEmail?: string;
  eventTitle?: string;
  agentVersion?: string;
  internalIPs?: string[];
  internalMdns?: string;
  gatewayIPs?: string[];
  externalIPs?: string[];
  iceCandidates?: RTCIceCandidateInit[];
  gatherDuration: LocalityGatherDuration;
  probeUrls?: PingResponse[];
  hasProbed?: boolean;
  sessionPlayType?: PlayType;
}

export const isLocalityConstructor = (
  obj?: unknown
): obj is LocalityConstructor => {
  const data = obj as LocalityConstructor;
  return data && (!!data.eleven || !!data.ice || !!data.probe);
};

export default class LocalityKey implements ILocality {
  machineName?: string;
  machineUser?: string;
  userEmail?: string;
  eventTitle?: string;
  agentVersion?: string;
  internalIPs?: string[];
  internalMdns?: string;
  gatewayIPs?: string[];
  externalIPs?: string[];
  iceCandidates?: RTCIceCandidateInit[];
  gatherDuration: LocalityGatherDuration;
  probeUrls?: PingResponse[];
  hasProbed?: boolean;
  sessionPlayType?: PlayType;

  // If we have 11 agent data it takes precedence over 10 agent data
  constructor(data?: LocalityConstructor | ILocality) {
    if (!isLocalityConstructor(data)) {
      const obj = data || ({} as ILocality);
      this.agentVersion = obj.agentVersion;
      this.machineName = obj.machineName;
      this.machineUser = obj.machineUser;
      this.eventTitle = obj.eventTitle;
      this.userEmail = obj.userEmail;
      this.internalIPs = obj.internalIPs;
      this.internalMdns = obj.internalMdns;
      this.gatewayIPs = obj.gatewayIPs;
      this.externalIPs = obj.externalIPs;
      this.iceCandidates = obj.iceCandidates;
      this.gatherDuration = obj.gatherDuration || { ...DEFAULT_DURATIONS };
      this.probeUrls = obj.probeUrls;
      this.hasProbed = obj.hasProbed;
      this.sessionPlayType = obj.sessionPlayType;
      return;
    }
    const obj = data || ({} as LocalityConstructor);
    const eleven = obj.eleven || ({} as ElevenAgentData);
    this.agentVersion = eleven.version;
    this.machineName = eleven.host;
    this.machineUser = eleven.user;
    this.userEmail = obj.userEmail;
    this.eventTitle = obj.eventTitle;
    mergeInAddresses(this, eleven);
    this.iceCandidates = obj.ice;
    this.gatherDuration = obj.gatherDuration || { ...DEFAULT_DURATIONS };
    this.probeUrls = obj.probe && obj.probe.probeUrls;
    this.hasProbed = obj.probe && obj.probe.hasProbed;
  }

  mergeNetworkData(data?: NetworkData): void {
    if (!data) {
      return;
    }
    this.internalIPs = appendItem(this.internalIPs, data.internalIps);
    this.externalIPs = appendItem(this.externalIPs, data.externalIps);
    if (!this.internalMdns) {
      this.internalMdns = data.internalMdns;
    }
  }
}
